import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function RivenditoreCatalog() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [network, setNetwork] = useState(null);
  const [edited, setEdited] = useState({});

  const refresh = () => api.get("/catalog").then((r) => setCatalog(r.data));

  useEffect(() => {
    refresh();
    api.get(`/networks/${user.network_id}`).then((r) => setNetwork(r.data));
  }, [user.network_id]);

  const statuses = network?.vertical_config?.availability_statuses || [];

  const updateLocal = (sku, key, val) => {
    setEdited((e) => ({ ...e, [sku]: { ...e[sku], [key]: val } }));
  };

  const save = async (item) => {
    const changes = edited[item.sku_variante] || {};
    try {
      // Stock update separato (endpoint dedicato)
      if (changes.qta_propria !== undefined && changes.qta_propria !== item.qta_propria) {
        await api.put(`/catalog/${item.sku_variante}/stock`, { qta_propria: changes.qta_propria });
      }
      await api.put(`/catalog/${item.sku_variante}`, {
        sku_variante: item.sku_variante,
        visibile_in_vetrina: changes.visibile_in_vetrina ?? item.visibile_in_vetrina,
        prezzo_vendita_cliente: changes.prezzo_vendita_cliente ?? item.prezzo_vendita_cliente,
        sconto_percentuale: changes.sconto_percentuale ?? item.sconto_percentuale,
        stato_disponibilita: changes.stato_disponibilita ?? item.stato_disponibilita,
      });
      toast.success(t("saved"));
      setEdited((e) => { const { [item.sku_variante]: _, ...rest } = e; return rest; });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="riv-catalog-page">
        <div className="mb-10">
          <div className="overline text-brand mb-2">Rivenditore</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">{t("riv_catalog")}</h1>
          <p className="text-muted-foreground mt-2">Personalizza il tuo listino: visibilità, prezzo retail e sconti per ogni variante della Casa Madre.</p>
        </div>

        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-border">
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Prodotto</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">SKU</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="text-center px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Mio stock</th>
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">{t("riv_price")}</th>
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">{t("riv_discount")}</th>
                <th className="text-center px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">{t("riv_visible")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {catalog.map((c) => {
                const isEdited = !!edited[c.sku_variante];
                const editedVals = edited[c.sku_variante] || {};
                return (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-accent/20" data-testid={`catalog-row-${c.sku_variante}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {c.variante?.immagini_specifiche?.[0] && (
                          <img src={c.variante.immagini_specifiche[0]} alt="" className="w-12 h-12 object-cover rounded-sm" />
                        )}
                        <div>
                          <div className="font-medium text-sm">{c.prodotto?.nome}</div>
                          <div className="text-xs text-muted-foreground">
                            {Object.entries(c.variante?.custom_attributes || {}).slice(0, 3).map(([k, v]) => `${v}`).join(" · ")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{c.sku_variante}</td>
                    <td className="px-4 py-3">
                      <Select
                        value={editedVals.stato_disponibilita ?? c.stato_disponibilita}
                        onValueChange={(v) => updateLocal(c.sku_variante, "stato_disponibilita", v)}
                      >
                        <SelectTrigger className="h-9 rounded-sm text-xs w-[170px]" data-testid={`catalog-status-${c.sku_variante}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((s) => (
                            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        data-testid={`catalog-stock-${c.sku_variante}`}
                        type="number" min={0}
                        value={editedVals.qta_propria ?? (c.qta_propria || 0)}
                        onChange={(e) => updateLocal(c.sku_variante, "qta_propria", parseInt(e.target.value) || 0)}
                        className="w-16 h-9 px-2 rounded-sm border border-border text-sm text-center"
                      />
                      <div className="text-[9px] text-muted-foreground mt-0.5">CM: {c.stock?.qta_disponibile || 0}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        data-testid={`catalog-price-${c.sku_variante}`}
                        type="number"
                        value={editedVals.prezzo_vendita_cliente ?? c.prezzo_vendita_cliente}
                        onChange={(e) => updateLocal(c.sku_variante, "prezzo_vendita_cliente", parseFloat(e.target.value) || 0)}
                        className="w-24 h-9 px-2 rounded-sm border border-border text-sm text-right"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        data-testid={`catalog-discount-${c.sku_variante}`}
                        type="number"
                        value={editedVals.sconto_percentuale ?? c.sconto_percentuale}
                        onChange={(e) => updateLocal(c.sku_variante, "sconto_percentuale", parseFloat(e.target.value) || 0)}
                        className="w-20 h-9 px-2 rounded-sm border border-border text-sm text-right"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        data-testid={`catalog-visible-${c.sku_variante}`}
                        checked={editedVals.visibile_in_vetrina ?? c.visibile_in_vetrina}
                        onCheckedChange={(v) => updateLocal(c.sku_variante, "visibile_in_vetrina", v)}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEdited && (
                        <button
                          data-testid={`catalog-save-${c.sku_variante}`}
                          onClick={() => save(c)}
                          className="text-xs px-3 h-9 inline-flex items-center gap-1.5 rounded-sm bg-brand hover:bg-brand-hover text-white transition-colors"
                        >
                          <Save className="w-3.5 h-3.5" /> {t("riv_save")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
